<script setup lang="ts">
import { ref, watch, computed, onMounted, nextTick } from 'vue'
import { FileUpload, useFileUpload } from '@ark-ui/vue'
type FileUploadFileError = 'TOO_MANY_FILES' | 'FILE_INVALID_TYPE' | 'FILE_TOO_LARGE' | 'FILE_TOO_SMALL' | 'FILE_INVALID' | 'FILE_EXISTS'
import UiButton from './UiButton/index'
import UiIcon from './UiIcon.vue'
import UiText from './UiText.vue'
import { useMediaUpload } from '../composables/useMediaUpload'

export interface UiSingleFileUploadProps {
    name?: string
    id?: string
    accept?: Record<string, string[]> | string | string[]
    maxFileSize?: number
    minFileSize?: number
    disabled?: boolean
    hint?: string
    error?: string
    invalid?: boolean
    aspectRatio?: string | number
    initialUrls?: string[]
    initialItems?: UiFileUploadItem[]
    uploadFn?: (file: File, collection: string, metadata?: object) => Promise<{ id: string; url: string; filename?: string; metadata?: Record<string, unknown> }>
    deleteFn?: (item: UiFileUploadItem) => Promise<void>
    collection?: string
    metadata?: object
    autoUpload?: boolean
    refreshInterval?: number
    title?: string
    description?: string
    buttonText?: string
    note?: string
    dropzoneText?: string
}

const props = withDefaults(defineProps<UiSingleFileUploadProps>(), {
    title: 'Upload image',
    description: '',
    buttonText: 'Upload Image',
    note: '',
    dropzoneText: 'Drag and drop or click to upload your image file',
    aspectRatio: '16:9',
    autoUpload: false,
    collection: 'default',
    refreshInterval: 86400000,
})

export interface UiFileUploadItem {
    file?: File;
    url?: string;
    media_id?: string;
    filename?: string;
    metadata?: Record<string, unknown>;
    uploadedAt?: Date | string;
    isTemporary?: boolean;
}

const model = defineModel<UiFileUploadItem[]>({ default: [] })

const acceptedFiles = computed(() => fileUploadApi.value.acceptedFiles)
const errorMsg = ref<string | undefined>()

const fileUploadApi = useFileUpload({
    accept: props.accept as any,
    maxFiles: 1,
    maxFileSize: props.maxFileSize,
    minFileSize: props.minFileSize,
    disabled: props.disabled,
    name: props.name,
    id: props.id || props.name,
    onFileReject,
    onFileAccept: () => { errorMsg.value = undefined },
})

const mediaUpload = props.autoUpload ? useMediaUpload({
    uploadFn: props.uploadFn,
    deleteFn: props.deleteFn,
    collection: props.collection,
    metadata: props.metadata,
    refreshInterval: props.refreshInterval,
}) : null

const loadedUrls = ref<Set<string>>(new Set())

function isUrlLoaded(url: string): boolean {
    return loadedUrls.value.has(url)
}

function filesSignature(files: File[]): string {
    return files.map(f => `${f.name}-${f.size}-${(f as any).lastModified ?? ''}`).join('|')
}

watch(acceptedFiles, async (files) => {
    if (!files) return

    const nextFile = files[0]
    const urlItems = (model.value || []).filter(i => i.url && !i.file)
    const previousItem = urlItems[0]

    if (!nextFile) {
        model.value = urlItems.slice(0, 1)
        return
    }

    if (props.autoUpload && mediaUpload) {
        await mediaUpload.handleFileSelection([nextFile], (uploadedItems) => {
            if (previousItem && previousItem.media_id !== uploadedItems[0]?.media_id) {
                mediaUpload.handleFileRemoval(previousItem, () => {})
            }
            model.value = [...urlItems.slice(0, 1), ...uploadedItems.slice(0, 1)].slice(-1)
        })
        return
    }

    const currentFileItems = (model.value || []).filter(i => i.file).map(i => i.file as File)
    if (filesSignature([nextFile]) === filesSignature(currentFileItems)) return
    model.value = [...urlItems.slice(0, 1), { file: nextFile }].slice(-1)
})

watch(model, (items, oldItems) => {
    if (!items) return

    const files = items.filter(i => i.file).map(i => i.file as File).slice(0, 1)
    if (filesSignature(files) !== filesSignature(acceptedFiles.value)) {
        fileUploadApi.value.setFiles(files)
    }

    if (items.length === 0 && oldItems && oldItems.length > 0) {
        nextTick(() => {
            fileUploadApi.value.clearFiles()
            errorMsg.value = undefined
        })
    }
}, { immediate: true, deep: true })

function onFileReject(details: any) {
    const rejected = (details && (details.rejectedFiles || details.files || [])) as Array<{ file: File; errors: FileUploadFileError[] }>
    if (!rejected.length) return
    const errorSet = new Set<FileUploadFileError>(rejected.flatMap(r => r.errors))
    if (errorSet.has('TOO_MANY_FILES')) {
        errorMsg.value = 'You can only upload one image.'
        return
    }
    if (errorSet.has('FILE_INVALID_TYPE')) {
        errorMsg.value = 'Invalid file type.'
        return
    }
    if (errorSet.has('FILE_TOO_LARGE')) {
        errorMsg.value = 'File too large.'
        return
    }
    if (errorSet.has('FILE_TOO_SMALL')) {
        errorMsg.value = 'File too small.'
        return
    }
    errorMsg.value = 'Some files were rejected.'
}

const aspectRatioStyle = computed(() => {
    const v = props.aspectRatio ?? '16:9'
    if (typeof v === 'number') return { aspectRatio: `${v}` }
    const normalized = v.replace(':', '/').replace(/\s+/g, '')
    const css = normalized.includes('/') ? normalized.replace('/', ' / ') : normalized
    return { aspectRatio: css }
})

const activeItem = computed(() => model.value?.[0])

onMounted(() => {
    if (props.initialItems && props.initialItems.length) {
        const existing = model.value || []
        const toAdd = props.initialItems.filter(item =>
            !existing.some(it => it.url === item.url || it.media_id === item.media_id)
        )
        if (toAdd.length) model.value = [...existing, ...toAdd].slice(0, 1)
    } else if (props.initialUrls && props.initialUrls.length) {
        const existing = model.value || []
        const toAdd = props.initialUrls
            .filter(url => !existing.some(it => it.url === url))
            .map(url => ({ url }))
        if (toAdd.length) model.value = [...existing, ...toAdd].slice(0, 1)
    }

    if (!props.autoUpload || !mediaUpload || !props.refreshInterval) return

    for (const item of model.value || []) {
        if (!item.media_id || !item.isTemporary) continue
        mediaUpload.trackTemporaryMedia(item)
    }

    mediaUpload.startExpirationCheck(props.refreshInterval, (oldMediaId, newMediaId, newUrl) => {
        model.value = (model.value || []).map(item => {
            if (item.media_id === oldMediaId) {
                return {
                    ...item,
                    media_id: newMediaId,
                    url: newUrl,
                    uploadedAt: new Date(),
                    isTemporary: true,
                }
            }
            return item
        })
    })
})
</script>

<template>
    <FileUpload.RootProvider :value="fileUploadApi" class="w-full">
        <div class="grid gap-4 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)] lg:items-center">
            <div class="flex flex-col h-full justify-between gap-4">
                <div class="flex flex-col gap-2">
                    <UiText as="h4" variant="h6" class="font-semibold text-se-dark" :text="props.title" />
                    <UiText
                        v-if="props.description"
                        as="p"
                        variant="small"
                        class="text-se-gray-700"
                        :text="props.description"
                    />
                </div>

                <div class="flex flex-col gap-1">
                    <FileUpload.Trigger class="w-fit">
                        <UiButton :label="props.buttonText" variant="primary" size="sm" :disabled="props.disabled">
                            <template #before_label>
                                <UiIcon name="upload" class="h-5 w-5" />
                            </template>
                        </UiButton>
                    </FileUpload.Trigger>

                    <UiText
                        v-if="props.note"
                        as="p"
                        variant="small"
                        class="text-se-gray-700"
                        :text="props.note"
                    />
                </div>
            </div>

            <FileUpload.Dropzone
                class="relative flex w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg border border-dashed border-se-gray-500 bg-white"
                :style="aspectRatioStyle"
                :disableClick="!!props.disabled"
                :allowDrop="!props.disabled"
            >
                <template v-if="activeItem">
                    <template v-if="activeItem.url && (!activeItem.file || isUrlLoaded(activeItem.url))">
                        <img :src="activeItem.url" class="absolute inset-0 h-full w-full object-cover" @load="() => loadedUrls.add(activeItem.url!)" />
                    </template>
                    <FileUpload.ItemGroup v-else-if="activeItem.file && acceptedFiles.length > 0" class="absolute inset-0 h-full w-full">
                        <FileUpload.Item v-if="acceptedFiles[0]" :file="acceptedFiles[0]" class="h-full w-full">
                            <FileUpload.ItemPreview type="image/*" class="block h-full w-full">
                                <FileUpload.ItemPreviewImage class="h-full w-full object-cover" />
                            </FileUpload.ItemPreview>
                        </FileUpload.Item>
                    </FileUpload.ItemGroup>
                    <img
                        v-if="activeItem.url && activeItem.file && !isUrlLoaded(activeItem.url)"
                        :src="activeItem.url"
                        class="hidden"
                        @load="() => loadedUrls.add(activeItem.url!)"
                    />
                </template>

                <div class="relative z-10 mx-auto max-w-[320px] px-6 text-center" v-if="!activeItem">
                    <UiText as="p" variant="h6" class="font-semibold text-se-dark">
                        {{ props.dropzoneText }}
                    </UiText>
                </div>
            </FileUpload.Dropzone>
        </div>

        <FileUpload.HiddenInput />

        <UiText
            v-if="errorMsg || props.error"
            as="p"
            variant="small"
            class="mt-2 text-red-600"
            :text="errorMsg || props.error"
        />
        <UiText
            v-else-if="props.hint"
            as="p"
            variant="small"
            class="mt-2 text-se-gray-700"
            :text="props.hint"
        />
    </FileUpload.RootProvider>
</template>
